<template>
  <div class="main-content-container overflow-hidden">
    <PageTitle pageTitle="Dashboard" subTitle="Overview" />

    <div v-if="showAlert" class="alert alert-warning text-warning mb-4 mx-auto" style="max-width: 1200px;" role="alert">
      <i class="ri-error-warning-line fs-18 me-1"></i>
      <span v-html="alertMessage"></span>
    </div>

    <div class="row justify-content-center">
      <div class="col-lg-12">
        <div class="border-0">
          <div class="p-0">
            <div class="d-flex justify-content-between align-items-center  pb-20">
             
            </div>

            <!-- Loading state -->
            <div v-if="loading" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Caricamento...</span>
              </div>
            </div>

            <!-- Show last calculation results if available -->
            <div v-else-if="lastCalculation">
              <!-- Tax Dashboard Component -->
              <div class="row">
                <div class="col-12">
                  <TaxDashboard :lastCalculation="lastCalculation" :hideTaxWidgets="shouldHideTaxWidgets" />
                </div>
              </div>
            </div>

            <!-- Show default content if no calculations -->
            <div v-else class="row justify-content-center">
              <div class="col-md-6 mb-4">
                <div class="card border-0 rounded-10 h-100" style="background-color: #f8f9fa;">
                  <div class="card-body p-4 text-center">
                    <span class="material-symbols-outlined text-primary mb-3" style="font-size: 48px;">calculate</span>
                    <h5 class="fw-semibold mb-2">Calcola Rata</h5>
                    <p class="text-muted mb-3">Calcola le tue tasse e pianifica i pagamenti</p>
                    <RouterLink to="/calcolo/forfettario" class="btn btn-primary">
                      Inizia Calcolo
                    </RouterLink>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch, nextTick } from "vue";
import { useRoute, useRouter } from 'vue-router';
import { Modal } from 'bootstrap';
import PageTitle from "@/components/Common/PageTitle.vue";
import authService, { type LastCalculationData } from "@/services/authService";
import TaxDashboard from "@/components/Modules/TaxCalculation/TaxDashboard.vue";
import { authStore } from "@/stores/auth";

export default defineComponent({
  name: "DashboardPage",
  components: {
    PageTitle,
    TaxDashboard,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const lastCalculation = ref<LastCalculationData | null>(null);
    const loading = ref(true);
    const showAlert = ref(false);
    const alertMessage = ref('');

    const COMING_SOON_REGIMES = [
      // TODO
      'GESTIONE_SEPARATA_EX_INPGI',
      'IVS_COMMERCIANTI_ENASARCO',
    ];

    const COMING_SOON_CATEGORIES = [
      'GIORNALISTA',
      'INTERMEDIARIES',
    ];

    const fetchLastCalculation = async () => {
      try {
        const response = await authService.getLastCalculation();
        if (response.data.success && response.data.data?.last_calculation) {
          lastCalculation.value = response.data.data.last_calculation;
        }
      } catch (error: any) {
        // If 404 (no calculations found), that's fine - user hasn't calculated yet
        if (error.response?.status !== 404) {
          console.error('Error fetching last calculation:', error);
        }
      } finally {
        loading.value = false;
      }
    };

    // Computed property per verificare se nascondere i tax widgets specifici
    const shouldHideTaxWidgets = computed(() => {
      if (!lastCalculation.value) {
        return false;
      }

      // Verifica se il calcolo è precedente al 22 gennaio 2026 ore 10:00
      const cutoffDate = new Date('2026-01-22T10:00:00');
      const calculationDate = new Date(lastCalculation.value.created_at);

      const shouldHide = calculationDate <= cutoffDate;

      if (shouldHide) {
        console.log('Tax widgets (TaxBreakdownChart e DeadlinesWidget) nascosti - calcolo precedente a cutoff. Data calcolo:', lastCalculation.value.created_at);
      }

      return shouldHide;
    });

    onMounted(async () => {
      console.log('1. DashboardPage mounted, route.query:', route.query);

      fetchLastCalculation();

      // Attendi il prossimo tick per assicurarci che il DOM sia aggiornato
      await nextTick();
      console.log('2. After nextTick');

      // Controlla se c'è il query parameter per aprire la modale
      const openAuth = route.query.openAuth as string;
      console.log('3. openAuth value:', openAuth);

      if (openAuth) {
        console.log('4. Inside openAuth condition, checking auth status');
        console.log('5. isLoggedIn:', authStore.isLoggedIn.value);

        // Se l'utente è già loggato, rimuovi solo il query parameter senza aprire la modale
        if (authStore.isLoggedIn.value) {
          console.log('6. User already logged in, just cleaning query params');
          router.replace({ query: {} });
          return;
        }

        console.log('7. User not logged in, opening auth modal');

        // Imposta il tab corretto immediatamente
        window.dispatchEvent(new CustomEvent('setAuthModalTab', { detail: { tab: openAuth } }));
        console.log('8. Event dispatched for tab:', openAuth);

        // Attendi che la modale sia renderizzata e poi aprila
        console.log('9. Setting timeout...');
        setTimeout(() => {
          console.log('10. ✓ TIMEOUT EXECUTED - inside callback');

          // Rimuovi il query parameter dalla URL ora che siamo nel timeout
          router.replace({ query: {} });
          console.log('11. Route replaced, query params removed');

          const modalElement = document.getElementById('authModal');
          console.log('12. Modal element found:', !!modalElement);

          if (modalElement) {
            try {
              const modal = new Modal(modalElement);
              console.log('13. Modal instance created, calling show()...');
              modal.show();
              console.log('14. ✓ Modal.show() called successfully');
            } catch (error) {
              console.error('15. Error opening modal:', error);
            }
          } else {
            console.error('16. Modal element not found!');
          }
        }, 1500);
        console.log('17. Timeout set, waiting for execution...');
      }

      console.log('15. onMounted completed');
    });

    watch(lastCalculation, (newVal) => {
      if (newVal && newVal.input_data) {
        const regime = newVal.input_data.contributionRegime;
        const category = newVal.input_data.macroCategory; // Assuming macroCategory maps to category

        if (COMING_SOON_REGIMES.includes(regime) || COMING_SOON_CATEGORIES.includes(category)) {
          alertMessage.value = '<strong>Attenzione!</strong> Il regime contributivo o la categoria selezionata nell\'ultima simulazione è "presto disponibile". Il calcolo potrebbe non essere preciso. Verrai contattato quando sarà ultimato.';
          showAlert.value = true;
        } else {
          showAlert.value = false;
          alertMessage.value = '';
        }
      } else {
        showAlert.value = false;
        alertMessage.value = '';
      }
    }, { immediate: true }); // Check immediately if lastCalculation is already set

    return {
      lastCalculation,
      loading,
      showAlert,
      alertMessage,
      shouldHideTaxWidgets,
    };
  },
});
</script>